import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type { CalculatorBootstrap, CalculatorConfig, ControlStyle, FieldValues, Formatters, IrsLimitsSet, StepConfig } from '../types';
import { fmt as formatters } from '../lib/format';
import {
  calculatorReducer,
  createInitialState,
  getStepErrors,
  getVisibleSteps,
  type EngineAction,
  type EngineState,
} from './reducer';

interface DerivedState<TValues extends FieldValues> {
  visibleSteps: StepConfig<TValues>[];
  currentStep: StepConfig<TValues> | null;
  stepErrors: Partial<Record<keyof TValues, string>>;
  resultsVisible: boolean;
}

interface CalculatorContextValue<TValues extends FieldValues, TResult> {
  state: EngineState<TValues>;
  config: CalculatorConfig<TValues, TResult>;
  limits: IrsLimitsSet;
  options: CalculatorBootstrap['options'];
  controlStyle: ControlStyle;
  derived: DerivedState<TValues>;
  setValue: <K extends keyof TValues>(name: K, value: TValues[K]) => void;
  blur: (name: keyof TValues) => void;
  attemptAdvance: () => void;
  back: () => void;
  goToStep: (stepId: string) => void;
  reset: () => void;
  fmt: Formatters;
}

// The context is intentionally untyped at the module boundary — every
// consumer goes through useCalculator<TValues, TResult>(), which casts to
// the calculator-specific shape. A generic React Context cannot itself be
// parameterized per-render, so this is the standard escape hatch.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CalculatorContext = createContext<CalculatorContextValue<any, any> | null>(null);

export function CalculatorProvider<TValues extends FieldValues, TResult>({
  config,
  limits,
  options,
  controlStyle,
  children,
}: {
  config: CalculatorConfig<TValues, TResult>;
  limits: IrsLimitsSet;
  options: CalculatorBootstrap['options'];
  controlStyle: ControlStyle;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(
    (s: EngineState<TValues>, a: EngineAction<TValues>) => calculatorReducer(s, a, { config, limits }),
    config,
    createInitialState,
  );

  const derived = useMemo<DerivedState<TValues>>(() => {
    const visibleSteps = getVisibleSteps(config.steps, state.values);
    const currentStep = state.phase === 'steps' ? (visibleSteps[state.stepIndex] ?? null) : null;
    const stepErrors = currentStep
      ? getStepErrors(currentStep, state.values, { limits, values: state.values })
      : {};
    return { visibleSteps, currentStep, stepErrors, resultsVisible: state.phase === 'results' };
  }, [config.steps, state.values, state.phase, state.stepIndex, limits]);

  const value = useMemo<CalculatorContextValue<TValues, TResult>>(
    () => ({
      state,
      config,
      limits,
      options,
      controlStyle,
      derived,
      setValue: (name, val) => dispatch({ type: 'setValue', name, value: val }),
      blur: (name) => dispatch({ type: 'blur', name }),
      attemptAdvance: () => dispatch({ type: 'attemptAdvance' }),
      back: () => dispatch({ type: 'back' }),
      goToStep: (stepId) => dispatch({ type: 'goToStep', stepId }),
      reset: () => dispatch({ type: 'reset' }),
      fmt: formatters,
    }),
    [state, config, limits, options, controlStyle, derived],
  );

  return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
}

export function useCalculator<
  TValues extends FieldValues = FieldValues,
  TResult = unknown,
>(): CalculatorContextValue<TValues, TResult> {
  const ctx = useContext(CalculatorContext);
  if (!ctx) throw new Error('useCalculator must be used within a CalculatorProvider');
  return ctx as CalculatorContextValue<TValues, TResult>;
}

export function useIrsLimits(): IrsLimitsSet {
  return useCalculator().limits;
}

/** Reads and writes one field's value with validation/touched state resolved. */
export function useField<TValues extends FieldValues, K extends keyof TValues>(name: K) {
  const { state, setValue, blur, derived } = useCalculator<TValues>();
  const stepId = derived.currentStep?.id ?? '__intro__';
  const submitAttempted = !!state.submitAttempted[stepId];
  const touched = !!state.touched[name];
  const rawError = derived.stepErrors[name] ?? null;
  const error = rawError && (touched || submitAttempted) ? rawError : null;

  return {
    value: state.values[name],
    setValue: (v: TValues[K]) => setValue(name, v),
    blur: () => blur(name),
    error,
    touched,
  };
}
